import { format } from 'date-fns';
import { isNil } from 'lodash';
import '../../extensions/number-currency-format';
import { IAPFREQUENCY } from 'bff/client/models/IAPFREQUENCY';
import Big from 'big.js';

export const convertToCamelCase = (value: string | undefined) => {
  if (!isNil(value)) {
    return value.charAt(0).toLocaleUpperCase() + value.slice(1).toLocaleLowerCase();
  }
  return undefined;
};

export const convertUnderscoreSplitOrWordToCamelCase = (value: string) => {
  const splitValues = value.split('_').map((word) => {
    return convertToCamelCase(word);
  });
  return splitValues.join(' ');
};

export const convertToHumanReadableText = (value: string | number | Date | undefined) => {
  if (!isNil(value) && typeof value === 'string')
    return convertUnderscoreSplitOrWordToCamelCase(value as string);
  return undefined;
};

export const convertToCurrency = (value: string | number | Date | undefined) => {
  if (!isNil(value)) {
    return Number(value).ToCurrencyString();
  }
  return undefined;
};

export const formatToCurrency = (value: string | number | Date | undefined) => {
  if (isNil(value) || Number(value) === 0) {
    return '-';
  }
  return Number(value).ToCurrencyString();
};

export const formatToRoundedCurrency = (value: string | number | Date | undefined) => {
  if (isNil(value) || Number(value) === 0) {
    return '-';
  }
  return Number(Big(Number(value)).round(2).toNumber()).ToCurrencyString();
};

export const formatToCurrencyAllowZero = (value: string | number | Date | undefined) => {
  if (isNil(value)) {
    return '-';
  }
  return Number(value).ToCurrencyString();
};

export const convertToPercentage = (value: string | number | Date | undefined) => {
  if (!isNil(value)) return `${value}%`;
  return undefined;
};

export const convertToNumberOfYears = (value: string | number | Date | undefined) => {
  if (!isNil(value)) return `${value} years`;
  return undefined;
};

export const convertToNumberOfYearsGuaranteedTerm = (value: string | number | Date | undefined) => {
  if (value === '0') return 'Until death';
  if (!isNil(value)) return `${value} years`;
  return undefined;
};

export const formatDateAsString = (value?: Date) => {
  if (value) return format(value, 'yyyy - MM - dd');

  return undefined;
};

export const formatTimeAsString = (value?: Date) => {
  if (value) return format(value, 'HH:mm:ss');
  return undefined;
};

export const formatTimeAsStringWithoutSeconds = (value?: Date) => {
  if (value) return format(value, 'HH:mm');
  return undefined;
};

export const formatDateTimeAsString = (value?: Date) => {
  return `${formatDateAsString(value)?.replaceAll(' ', '')} ${formatTimeAsString(value)}`;
};

export const formatDateWithMonthNameString = (value?: Date) => {
  if (value) return `${format(value, 'dd MMMM yyyy')}`;
  return undefined;
};

export const convertPascalCaseToHumanReadableText = (value?: string) => {
  if (!isNil(value)) {
    return value.replace(/([A-Z])/g, ' $1').trim();
  }
  return undefined;
};

export const formatContactNumber = (value: string) => {
  const phoneNumber = value.match(/(\d)/g)?.join('');
  if (phoneNumber === undefined) return '';
  if (phoneNumber.length === 9) {
    return phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3');
  }
  if (phoneNumber.length >= 10) {
    return phoneNumber.replace(/(\d{3})(\d{3})(\d{4,})/, '$1 $2 $3');
  }

  return phoneNumber;
};

export const displayNumberAtTwoDecimalPlaces = (value: number) => {
  return (Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2);
};

export const formatDateMonthName = (value?: Date) => {
  if (value) return `${format(value, 'MMMM')}`;
  return undefined;
};

export const getOccurrenceFromFrequency = (frequency?: IAPFREQUENCY | null): number => {
  switch (frequency) {
    case IAPFREQUENCY.Monthly:
      return 12;
    case IAPFREQUENCY.Quarterly:
      return 4;
    case IAPFREQUENCY.HalfYearly:
      return 2;
    case IAPFREQUENCY.Yearly:
      return 1;
    default:
      return 0;
  }
};

export const getOccurrenceTextFromFrequency = (freq?: IAPFREQUENCY | null): string => {
  switch (freq) {
    case IAPFREQUENCY.Monthly:
      return 'per month';
    case IAPFREQUENCY.Quarterly:
      return 'per quarter';
    case IAPFREQUENCY.HalfYearly:
      return 'half-yearly';
    case IAPFREQUENCY.Yearly:
      return 'per year';
    default:
      return '';
  }
};

export const getLabelFromFrequency = (freq?: IAPFREQUENCY | null): string => {
  switch (freq) {
    case IAPFREQUENCY.Monthly:
      return 'monthly';
    case IAPFREQUENCY.Quarterly:
      return 'quarterly';
    case IAPFREQUENCY.HalfYearly:
      return 'half-yearly';
    case IAPFREQUENCY.Yearly:
      return 'yearly';
    default:
      return '';
  }
};
